// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Payment panel (type buttons + numpad + add button)
// ---------------------------------------------------------------------------

import { usePaymentStore } from '@/store/payment-store';
import { useCartStore } from '@/store/cart-store';
import { useAppStore } from '@/store/app-store';
import type { PaymentType } from '@/lib/types';
import { PAYMENT_TYPES } from '@/lib/constants';
import NumPad from '@/components/NumPad';

const TYPE_LABELS: Record<PaymentType, string> = {
  CB: 'CB',
  ESPECES: 'Especes',
  PREVENTE: 'Prevente',
};

const TYPE_STYLES: Record<PaymentType, { active: string; inactive: string }> = {
  CB: {
    active: 'bg-info text-white',
    inactive: 'bg-surface-card text-info hover:bg-info/20',
  },
  ESPECES: {
    active: 'bg-success text-white',
    inactive: 'bg-surface-card text-success hover:bg-success/20',
  },
  PREVENTE: {
    active: 'bg-warning text-white',
    inactive: 'bg-surface-card text-warning hover:bg-warning/20',
  },
};

export default function PaymentPanel() {
  const selectedType = usePaymentStore((s) => s.selectedType);
  const selectType = usePaymentStore((s) => s.selectType);
  const setExactAmount = usePaymentStore((s) => s.setExactAmount);
  const getAmountAsNumber = usePaymentStore((s) => s.getAmountAsNumber);
  const reset = usePaymentStore((s) => s.reset);

  const addPayment = useCartStore((s) => s.addPayment);
  const getRemainingAmount = useCartStore((s) => s.getRemainingAmount);

  const showToast = useAppStore((s) => s.showToast);

  const remaining = getRemainingAmount();
  const amount = getAmountAsNumber();
  const canAdd = amount > 0 && selectedType !== null && amount <= remaining + 0.01;

  function handleAddPayment() {
    if (!selectedType) {
      showToast('Selectionnez un moyen de paiement', 'warning');
      return;
    }
    if (amount <= 0) {
      showToast('Montant invalide', 'warning');
      return;
    }
    if (amount > remaining + 0.01) {
      showToast('Montant superieur au reste a payer', 'warning');
      return;
    }
    addPayment(selectedType, amount);
    reset();
    showToast('Paiement ajoute', 'success');
  }

  return (
    <div className="space-y-3">
      {/* Payment type buttons */}
      <div className="flex gap-2">
        {PAYMENT_TYPES.map((type) => {
          const isActive = selectedType === type;
          const styles = TYPE_STYLES[type];
          return (
            <button
              key={type}
              type="button"
              onClick={() => selectType(type)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                isActive ? styles.active : styles.inactive
              }`}
            >
              {TYPE_LABELS[type]}
            </button>
          );
        })}
      </div>

      {/* NumPad */}
      <NumPad onValidate={handleAddPayment} />

      {/* Exact amount + Add payment */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={setExactAmount}
          className="flex-1 btn-secondary text-xs py-2"
          disabled={remaining <= 0}
        >
          Montant exact
        </button>
        <button
          type="button"
          onClick={handleAddPayment}
          disabled={!canAdd}
          className="flex-1 btn-primary text-xs py-2"
        >
          Ajouter paiement
        </button>
      </div>
    </div>
  );
}
